import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import SearchableSelect from '@/Components/SearchableSelect';
import Avatar from '@/Components/Avatar';
import Pagination from '@/Components/Pagination';
import SearchInput from '@/Components/SearchInput';
import ConfirmModal from '@/Components/ConfirmModal';

export default function SuppliersIndex({ suppliers, countries, serviceTypes, filters }) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);

    const handleSearch = (value) => {
        router.get('/suppliers', { ...filters, search: value }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleFilter = (key, value) => {
        router.get('/suppliers', { ...filters, [key]: value }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleToggleStatus = (supplier) => {
        router.patch(`/suppliers/${supplier.id}/toggle-status`, {}, {
            preserveScroll: true,
        });
    };

    const handleDelete = () => {
        router.delete(`/suppliers/${supplierToDelete.id}`, {
            onSuccess: () => {
                setShowDeleteModal(false);
                setSupplierToDelete(null);
            },
        });
    };

    const serviceTypeOptions = Object.entries(serviceTypes).map(([key, label]) => ({
        id: key,
        name: label,
    }));

    return (
        <AppLayout title="Suppliers">
            <PageHeader
                title="Suppliers"
                subtitle={`${suppliers.total?.toLocaleString() || 0} suppliers`}
                breadcrumbs={[
                    { label: 'Travel' },
                    { label: 'Suppliers' },
                ]}
                actions={
                    <Link href="/suppliers/create" className="btn btn-primary">
                        <i className="bi bi-plus-lg"></i>
                        <span>Add Supplier</span>
                    </Link>
                }
            />

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <SearchInput
                                value={filters.search || ''}
                                onSearch={handleSearch}
                                placeholder="Name, code, contact..."
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Service Type</label>
                            <SearchableSelect
                                options={serviceTypeOptions}
                                value={filters.service_type || ''}
                                onChange={(value) => handleFilter('service_type', value)}
                                placeholder="All Types"
                                searchPlaceholder="Search..."
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Country</label>
                            <SearchableSelect
                                options={countries}
                                value={filters.country || ''}
                                onChange={(value) => handleFilter('country', value)}
                                placeholder="All Countries"
                                searchPlaceholder="Search countries..."
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Status</label>
                            <select
                                className="form-select"
                                value={filters.status || ''}
                                onChange={(e) => handleFilter('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Per Page</label>
                            <select
                                className="form-select"
                                value={filters.per_page || 15}
                                onChange={(e) => handleFilter('per_page', e.target.value)}
                            >
                                <option value="15">15</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <button
                                onClick={() => router.get('/suppliers')}
                                className="btn btn-outline-secondary w-100"
                                title="Clear all filters"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Suppliers Table */}
            <div className="card">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>Supplier</th>
                                <th>Service Types</th>
                                <th>Contact</th>
                                <th>Location</th>
                                <th className="text-center">Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {suppliers.data.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <div className="text-muted">
                                            <i className="bi bi-building fs-1 d-block mb-3"></i>
                                            No suppliers found
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                suppliers.data.map((supplier, index) => (
                                    <tr key={supplier.id}>
                                        <td className="text-muted">
                                            {(suppliers.current_page - 1) * suppliers.per_page + index + 1}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <Avatar name={supplier.name} size="md" />
                                                <div>
                                                    <div className="fw-semibold">{supplier.name}</div>
                                                    {supplier.code && (
                                                        <div className="text-muted small">{supplier.code}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {supplier.service_types?.map((type) => (
                                                    <span key={type} className="badge bg-light text-dark">
                                                        {serviceTypes[type] || type}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td>
                                            {supplier.contact_person && (
                                                <div className="small">
                                                    <div className="fw-medium">{supplier.contact_person}</div>
                                                    {supplier.contact_email && (
                                                        <div className="text-muted">{supplier.contact_email}</div>
                                                    )}
                                                    {supplier.contact_phone && (
                                                        <div className="text-muted">{supplier.contact_phone}</div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {supplier.country && (
                                                <span className="badge bg-light text-dark">
                                                    {supplier.country.name}
                                                </span>
                                            )}
                                            {supplier.city && (
                                                <div className="text-muted small">{supplier.city}</div>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <span className={`badge ${supplier.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                                {supplier.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <div className="btn-group">
                                                <Link
                                                    href={`/suppliers/${supplier.id}/edit`}
                                                    className="btn btn-sm btn-outline-secondary"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => handleToggleStatus(supplier)}
                                                    title={supplier.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    <i className={`bi ${supplier.is_active ? 'bi-toggle-on text-success' : 'bi-toggle-off'}`}></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => {
                                                        setSupplierToDelete(supplier);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    data={suppliers}
                    showPerPage
                    onPerPageChange={(value) => handleFilter('per_page', value)}
                />
            </div>

            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Supplier"
                message={`Are you sure you want to delete ${supplierToDelete?.name}? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />
        </AppLayout>
    );
}
